import { useState } from 'react';
import { Alert, Box, Button, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthScreen, authSubmitSx } from '../components/auth/AuthScreen';
import { PasswordField } from '../components/common/PasswordField';
import { useAuth } from '../hooks/useAuth';
import { APP_VERSION } from '../config/app';
import { startLoginPushPermission } from '../push/webPush';
import { loginLandingPath } from '../utils/navigation';

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

export function SignupPage() {
  const { user, signup, hydrating, submitting, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState(null);

  if (!hydrating && user) {
    return <Navigate to={loginLandingPath(from, user.role)} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLocalError(null);
    const nextName = name.trim();
    const nextEmail = email.trim();
    const nextPhone = phone.trim();
    if (!nextName) {
      setLocalError('Enter your name.');
      return;
    }
    if (!nextEmail.includes('@')) {
      setLocalError('Enter a valid email.');
      return;
    }
    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (nextPhone) {
      let digits = digitsOnly(nextPhone);
      if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
      if (digits.length !== 10) {
        setLocalError('Enter a valid 10-digit mobile number, or leave it blank.');
        return;
      }
    }

    const permissionWork = startLoginPushPermission();
    try {
      const created = await signup(
        {
          name: nextName,
          email: nextEmail,
          password,
          ...(nextPhone ? { phone: nextPhone } : {}),
        },
        { permissionWork },
      );
      if (created?.role !== 'reader') {
        setLocalError('Account was created but is not ready. Please sign in.');
        return;
      }
      navigate(loginLandingPath(from, created.role), { replace: true });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Sign up failed');
    }
  }

  return (
    <AuthScreen hydrating={hydrating}>
      {(stageSx) => (
        <>
          <Box sx={stageSx(80)}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ mb: 0.75, letterSpacing: '-0.03em', fontSize: { xs: '1.65rem', sm: '2rem' } }}
            >
              Create account
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.55, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              Join as a reader to like stories and get alerts. An admin can give you a staff role later.
            </Typography>
          </Box>

          {(localError || error) && (
            <Alert severity="error">{localError || error}</Alert>
          )}

          <Box component="form" onSubmit={onSubmit} sx={stageSx(140)}>
            <Stack spacing={2}>
              <TextField
                label="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                autoComplete="name"
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
              />
              <TextField
                label="Mobile"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                autoComplete="tel"
                inputMode="tel"
                helperText="Optional. You can also sign in with this number."
              />
              <PasswordField
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                helperText="At least 6 characters"
              />
              <PasswordField
                label="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                disabled={submitting}
                fullWidth
                sx={authSubmitSx}
              >
                {submitting ? 'Creating account…' : 'Create account'}
              </Button>
            </Stack>
          </Box>

          <Divider />
          <Stack spacing={1}>
            <Link component={RouterLink} to="/login" state={{ from }} underline="hover" fontWeight={600}>
              Already have an account? Sign in
            </Link>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Prasaar v{APP_VERSION}
          </Typography>
        </>
      )}
    </AuthScreen>
  );
}
